import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';

import {
  LmsPlacement,
  LmsRegistryService,
  MoodleCourse,
} from '../../../services/lms-registry.service';
import { Tutor } from '../../../services/tutor.service';

@Component({
  selector: 'app-placement-edit-dialog',
  templateUrl: './placement-edit-dialog.component.html',
  styleUrl: './placement-edit-dialog.component.scss',
})
export class PlacementEditDialogComponent implements OnInit {
  @Input() placement!: LmsPlacement;
  @Input() course!: MoodleCourse;
  @Input() tutors: Tutor[] = [];

  form!: FormGroup;
  saving = false;

  constructor(
    private fb: FormBuilder,
    private lms: LmsRegistryService,
    private toastr: NbToastrService,
    private dialogRef: NbDialogRef<PlacementEditDialogComponent>,
    private t: TranslateService,
  ) {}

  ngOnInit(): void {
    this.form = this.fb.group({
      bot_id: [(this.placement as any).bot_id ?? null],
      visible: [
        (this.placement as any).visible !== undefined
          ? (this.placement as any).visible
          : true,
      ],
      primary_color: [(this.placement as any).primary_color ?? null],
    });
  }

  tutorById(botId: string | null | undefined): Tutor | undefined {
    if (!botId) return undefined;
    return this.tutors.find((tutor) => tutor.id === botId);
  }

  tutorVisualName(tutor: Tutor | null | undefined): string | null {
    const visualName = (tutor?.visual_name || '').trim();
    if (!visualName) return null;
    const internalName = (tutor?.name || '').trim();
    return visualName === internalName ? null : visualName;
  }

  tutorFullLabel(tutor: Tutor | null | undefined): string {
    if (!tutor) return '';
    const visualName = this.tutorVisualName(tutor);
    return visualName ? `${tutor.name} · ${visualName}` : tutor.name;
  }

  cancel(): void {
    this.dialogRef.close(false);
  }

  private resolveError(err: any): string {
    const detail = err?.error?.detail;
    if (typeof detail === 'string' && detail.trim()) {
      return detail;
    }
    if (detail && typeof detail === 'object') {
      return detail.message || detail.code || this.t.instant('toasts.error');
    }
    return err?.message || this.t.instant('toasts.error');
  }

  save(): void {
    if (this.saving) return;
    this.saving = true;

    const v = this.form.value;
    const patch: any = {};
    if (v.bot_id) patch.bot_id = v.bot_id;
    if (typeof v.visible === 'boolean') patch.visible = v.visible;
    if (v.primary_color !== null && v.primary_color !== '')
      patch.primary_color = v.primary_color;

    const placementUuid =
      this.placement.moodle_placement_uuid || this.placement.id;
    if (!placementUuid) {
      this.saving = false;
      return;
    }

    this.lms.updatePlacement(placementUuid, patch).subscribe({
      next: () => {
        this.saving = false;
        this.dialogRef.close(true);
      },
      error: (err) => {
        this.saving = false;
        this.toastr.danger(this.resolveError(err), '');
      },
    });
  }
}
